const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3060';

export interface BusinessCard {
  id: string;
  slug: string;
  userId: string;
  displayName: string;
  romajiName?: string;
  title?: string;
  bio?: string;
  photoUrl?: string;
  email?: string;
  phone?: string;
  officeLocation?: string;
  workingHours?: string;
  socialLinks?: {
    linkedin?: string;
    twitter?: string;
    github?: string;
    website?: string;
    calendar?: string;
    instagram?: string;
    youtube?: string;
    discord?: string;
  };
  serviceLinks?: Array<{
    title: string;
    url: string;
    icon: string;
    description?: string;
    imageUrl?: string;
    displayType?: 'iframe' | 'image';
  }>;
  theme: 'MODERN' | 'CLASSIC' | 'BOLD' | 'MINIMAL';
  isVisible: boolean;
  visibilitySettings?: {
    email?: boolean;
    phone?: boolean;
    officeLocation?: boolean;
    workingHours?: boolean;
    socialLinks?: boolean;
    serviceLinks?: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

export type BusinessCardInput = Partial<Omit<BusinessCard, 'id' | 'slug' | 'userId' | 'createdAt' | 'updatedAt'>> & {
  // Allow slug regeneration on update when explicitly requested
  regenerateSlug?: boolean;
};

export interface CardAnalytics {
  total: number;
  viewsByDate: Record<string, number>;
  recent: Array<{
    id: string;
    viewedAt: string;
    ipAddress?: string;
    userAgent?: string;
    referer?: string;
  }>;
}

export const api = {
  // Public: Get card by slug
  getCardBySlug: async (slug: string): Promise<BusinessCard> => {
    const res = await fetch(`${API_URL}/public/card/${slug}`);
    if (!res.ok) throw new Error('Card not found');
    const data = await res.json();
    return data.data;
  },

  // Public: Get QR code
  getQRCode: async (slug: string): Promise<{ qrCode: string; url: string }> => {
    const res = await fetch(`${API_URL}/public/card/${slug}/qr`);
    if (!res.ok) throw new Error('Failed to generate QR code');
    return res.json();
  },

  // Auth: Get my card
  getMyCard: async (token: string): Promise<BusinessCard> => {
    const res = await fetch(`${API_URL}/api/v1/cards/me`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to get card');
    const data = await res.json();
    return data.data;
  },

  // Auth: Update my card
  updateCard: async (token: string, data: BusinessCardInput): Promise<BusinessCard> => {
    const res = await fetch(`${API_URL}/api/v1/cards/me`, {
      method: 'PATCH',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error('Failed to update card');
    const result = await res.json();
    return result.data;
  },

  // Auth: Get analytics
  getAnalytics: async (token: string, days: number = 30): Promise<CardAnalytics> => {
    const res = await fetch(`${API_URL}/api/v1/cards/analytics?days=${days}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to get analytics');
    const data = await res.json();
    return data.data;
  },

  // Auth: Toggle visibility
  toggleVisibility: async (token: string): Promise<BusinessCard> => {
    const res = await fetch(`${API_URL}/api/v1/cards/toggle-visibility`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Failed to toggle visibility');
    const data = await res.json();
    return data.data;
  },
};
